import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { faChevronDown } from '@fortawesome/sharp-regular-svg-icons';
import { ItemActions } from '@europrocurement/flexy-components';
import { PreFilterDatatable, PreFilterDatatableList } from '../DatatableHeader';
import { FlexyPreFilter } from '../FlexyPreFilter/FlexyPreFilter';
import { FlexyMobilePopover } from '../FlexyMobilePopover';
import { useFilterKeyboardShortcuts } from '../../hooks/useFilterKeyboardShortcuts';

export type FlexyPreFiltersProps = {
    filters: PreFilterDatatableList;
    current?: PreFilterDatatable;
    onClick: (preFilter: PreFilterDatatable) => void;
};

export const FlexyPreFilters: React.FunctionComponent<FlexyPreFiltersProps> = function (props) {
    const { filters, current, onClick } = props;
    const { palette, breakpoints } = useTheme();
    const downSmBreakpoint = useMediaQuery(breakpoints.down('sm'));
    const [preSelected, setPreSelected] = useState<PreFilterDatatable>();
    const [showPanel, setShowPanel] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const { items } = filters;
    const visibleItems = items.filter((item) => !item.hidden);
    const hiddenItems = items.filter((item) => item.hidden);
    const hasHidden = hiddenItems.length > 0;

    useEffect(() => {
        if (current !== undefined && current.label !== preSelected?.label) {
            setPreSelected(current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current]);

    const handleConfirmMobilePanel = () => {
        setShowPanel(false);

        if (preSelected) {
            onClick(preSelected);
        }
    };

    const handleCancelMobilePanel = () => {
        if (current) {
            setPreSelected(current);
        }

        setShowPanel(false);
    };

    const { submitLabel, cancelLabel } = useFilterKeyboardShortcuts(
        handleConfirmMobilePanel,
        handleCancelMobilePanel,
        showPanel,
    );

    if (downSmBreakpoint) {
        const btnStyle = {
            padding: '8px 24px',
            borderRadius: '12px',
            fontFamily: 'Inter',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '24px',
            textAlign: 'center',
        };

        const controls = (
            <Box
                sx={{
                    width: '100%',
                    padding: '10px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0 16px',
                    boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.1)',
                    marginTop: 'auto',
                }}
            >
                <Button
                    variant="text"
                    onClick={handleCancelMobilePanel}
                    color="primary"
                    sx={btnStyle}
                >
                    {cancelLabel || 'Fermer'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmMobilePanel}
                    color="primary"
                    sx={btnStyle}
                >
                    {submitLabel}
                </Button>
            </Box>
        );

        const heading = filters.label ? (
            <Typography
                variant="l2dcaption"
                component="h2"
                sx={{
                    color: palette.text.light,
                    padding: '10px 14px 0 14px',
                    lineHeight: '22px',
                }}
            >
                {filters.label}
            </Typography>
        ) : null;

        return (
            <>
                <FlexyMobilePopover
                    open={showPanel}
                    heading={heading}
                    controls={controls}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            overflowY: 'scroll',
                            padding: '10px',
                        }}
                    >
                        {items.map((preFilter) => {
                            const { label, count } = preFilter;

                            return (
                                <FlexyPreFilter
                                    label={label}
                                    key={label}
                                    current={
                                        preSelected !== undefined && preSelected.label === label
                                    }
                                    count={count}
                                    onClick={() => setPreSelected(preFilter)}
                                />
                            );
                        })}
                    </Box>
                </FlexyMobilePopover>
                <Box>
                    <Button
                        sx={{
                            display: 'flex',
                            gap: '0 8px',
                            alignItems: 'center',
                            fontSize: '16px',
                            fontWeight: '600',
                            lineHeight: '25.6px',
                            color: palette.text.primary,
                        }}
                        onClick={() => setShowPanel(true)}
                    >
                        {current !== undefined && current.label}
                        <FaOptionIcon
                            icon={faChevronDown}
                            sx={{ fontSize: '12px', lineHeight: '12px' }}
                        />
                    </Button>
                </Box>
            </>
        );
    }

    const renderPreFilter = (preFilter: PreFilterDatatable) => {
        const { label, count } = preFilter;

        return (
            <FlexyPreFilter
                label={label}
                key={label}
                current={current !== undefined && current.label === label}
                count={count}
                onClick={() => onClick(preFilter)}
            />
        );
    };

    return (
        <Box
            sx={{
                position: 'relative',
                minWidth: 0,
                ml: '25px',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    flexDirection: 'row',
                    alignItems: 'start',
                    width: '100%',
                    pr: hasHidden ? '44px' : 0,
                    pb: '12px',
                    scrollbarGutter: 'stable',
                    '&::-webkit-scrollbar': { height: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'grey.300',
                        borderRadius: '3px',
                    },
                    scrollbarColor: `${palette.grey[300]} transparent`,
                }}
            >
                <Box sx={{ display: 'flex', gap: '24px', flexShrink: 0 }}>
                    {visibleItems.map(renderPreFilter)}
                </Box>
                {hasHidden && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '24px',
                            flexShrink: 0,
                            alignItems: 'center',
                            overflow: 'hidden',
                            maxWidth: expanded ? '2000px' : '0',
                            transition: 'max-width 0.3s ease-in-out',
                        }}
                    >
                        <Box
                            sx={{
                                width: '1px',
                                bgcolor: palette.divider,
                                height: '20px',
                                flexShrink: 0,
                                ml: '24px',
                            }}
                        />
                        {hiddenItems.map(renderPreFilter)}
                    </Box>
                )}
            </Box>
            {hasHidden && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: '-10px',
                        top: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: palette.background.default,
                        zIndex: 1,
                        mb: '12px',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            right: '100%',
                            top: 0,
                            bottom: 0,
                            width: '40px',
                            background: `linear-gradient(to right, transparent, ${palette.background.default})`,
                            pointerEvents: 'none',
                        }}
                    />
                    <ItemActions
                        onExpand={() => setExpanded(!expanded)}
                        expanded={expanded}
                        expandTooltip={
                            expanded ? 'Masquer les pré-filtres' : 'Afficher les pré-filtres'
                        }
                        size={28}
                        iconSize="13px"
                    />
                </Box>
            )}
        </Box>
    );
};
